// tabs/LocationsTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Card, Group, Stack, Title, Text, Button, SimpleGrid,
  Divider, ActionIcon,
} from '@mantine/core';
import { IconPlus, IconRefresh, IconX } from '@tabler/icons-react';
import { Select } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useFacilitatorService, useLocationService, FacilitatorLocationView } from '@actschurch/ccms-sdk';
import ConfirmActionModal from './ConfirmActionModal';

// ─── Props ────────────────────────────────────────────────────────────────────
interface LocationsTabProps {
  userId: string;
  active: boolean;
  onFacilitatorChange?: () => void;
  onCountChange?: (newCount: number) => void;
}

// ─── LocationsTab ─────────────────────────────────────────────────────────────
// Displays locations assigned to a facilitator.
// Add via dropdown, remove via confirm modal.
export default function LocationsTab({ userId, active, onFacilitatorChange, onCountChange }: LocationsTabProps) {
  const locationService      = useLocationService();
  const facilitatorLocations = useFacilitatorService();

  // Assigned locations for this facilitator
  const [assignedLocations, setAssignedLocations]   = useState<FacilitatorLocationView[]>([]);
  // Full global locations list for the add-dropdown
  const [availableLocations, setAvailableLocations] = useState<any[]>([]);
  const [loading, setLoading]                       = useState(false);

  // Dropdown selection state
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  // Confirm-remove modal state (mirrors care support pattern)
  const [confirmOpen, setConfirmOpen]     = useState(false);
  const [confirmTitle, setConfirmTitle]   = useState('Confirm Remove');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null);

  // ── Fetch assigned locations ───────────────────────────────────────────────
  const fetchAssignedLocations = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const result = await facilitatorLocations.getLocations(userId);
      setAssignedLocations(result);
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to load assigned locations.');
    } finally {
      setLoading(false);
    }
  }, [userId, facilitatorLocations]);

  // ── Fetch global locations for the dropdown ────────────────────────────────
  useEffect(() => {
    locationService.getAll().then(setAvailableLocations).catch(console.error);
  }, [locationService]);

  // ── Only fetch assigned locations when this tab is active ─────────────────
  useEffect(() => {
    if (active) fetchAssignedLocations();
  }, [active, fetchAssignedLocations]);

  // ── Resolve the join-table row ID (NOT the locationID) ────────────────────
  // This is the key fix for the 409 error — the remove endpoint expects the
  // facilitatorLocation row ID, not the locationID itself.
  const resolveRowId = (loc: any): number =>
    loc.facilitatorLocationID   // most specific — try first
    ?? loc.facilitatorLocationId
    ?? loc.id                   // generic fallback
    ?? loc.locationID;          // last resort (causes 409 if used alone)

  // ── Resolve display name ───────────────────────────────────────────────────
  const resolveLabel = (loc: any): string =>
    loc.locationName ?? loc.name ?? `Location ${loc.locationID}`;

  // ── IDs already assigned — used to filter the dropdown ────────────────────
  const assignedIds = new Set(assignedLocations.map((l) => l.locationID));

  // ── Add location ──────────────────────────────────────────────────────────
  const handleAddLocation = async () => {
  if (!selectedLocationId || !userId) return;

  if (assignedIds.has(Number(selectedLocationId))) {
    toast.error('Duplicate', 'This location is already assigned.');
    return;
  }

  setLoading(true);
  try {
    await facilitatorLocations.addLocation(
      userId,
      { locationID: Number(selectedLocationId) } as any
    );
    setSelectedLocationId(null);
    await fetchAssignedLocations();
    onFacilitatorChange?.();
    toast.success('Location Added', 'Location assigned to facilitator.');
  } catch (e) {
    console.error(e);
    toast.error('Error', 'Failed to add location.');
  } finally {
    setLoading(false);
  }
};

  // ── Remove location — opens confirm modal with the row ID ─────────────────
  const handleRemoveLocation = (loc: any) => {
    const rowId = resolveRowId(loc);
    const name  = resolveLabel(loc);

    setConfirmTitle('Remove Location');
    setConfirmMessage(`Remove this location${name ? ` (${name})` : ''} from the facilitator?`);

    // Store the action closure — called when the user confirms
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        // Pass the join-table row ID, not locationID
        await facilitatorLocations.removeLocation(userId, rowId as any);
        await fetchAssignedLocations();
        onFacilitatorChange?.();
        toast.warning('Location Removed', `"${name}" has been removed.`);
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove location.');
      } finally {
        setLoading(false);
      }
    });

    setConfirmOpen(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Shared confirm modal — reused across all tabs */}
      <ConfirmActionModal
        opened={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      {/* Add location row */}
      <Group gap="sm" justify="space-between" mb="md">
        <Group gap="sm">
          <Select
            placeholder="Select a location to add"
            style={{ width: 300 }}
            data={availableLocations
              .filter((l) => !assignedIds.has(l.locationID))
              .map((l) => ({
                value: String(l.locationID),
                label: resolveLabel(l),
              }))}
            value={selectedLocationId}
            onChange={setSelectedLocationId}
            searchable
            clearable
          />
        </Group>
        <Group gap="sm">
        <ActionIcon
          variant="subtle"
          size="lg"
          onClick={fetchAssignedLocations}
          loading={loading}
          aria-label="Refresh locations"
        >
          <IconRefresh size={16} />
        </ActionIcon>

         <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddLocation}
            disabled={!selectedLocationId || loading}
            loading={loading}
          >
            Add Location
          </Button>
        </Group>
      </Group>

      <Divider mb="md" />

      {/* Assigned locations grid */}
      {assignedLocations.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No locations assigned.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          {assignedLocations.map((loc: any) => (
            // Key on locationID since that's always present on the view model
            <Card key={loc.locationID} withBorder radius="md" padding="lg">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div>
                  <Text fw={700}>{resolveLabel(loc)}</Text>
                  {loc.description && (
                    <Text size="sm" c="dimmed">{loc.description}</Text>
                  )}
                </div>
                <ActionIcon
                  variant="light"
                  color="red"
                  onClick={() => handleRemoveLocation(loc)}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </>
  );
}